import { Badge } from '@repo/ui/components/badge'
import { cn } from '@repo/ui/lib/utils'

import type { ComingSoonItem, LandingFeature } from './landingContent'
import SpotlightCard from '../SpotlightCard'
import {
  COMING_SOON,
  COMING_SOON_STATUS_LABELS,
  FEATURES,
  LANDING_ACCENTS,
} from './landingContent'

function FeatureCard({ feature }: { feature: LandingFeature }) {
  const accent = LANDING_ACCENTS[feature.accent]
  const Icon = feature.icon

  return (
    <SpotlightCard
      className="border-border-muted bg-surface-1/50 p-6 backdrop-blur-sm"
      spotlightColor={accent.spotlight}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div
          className={cn(
            'flex size-12 shrink-0 items-center justify-center rounded-lg',
            accent.chip,
          )}
        >
          <Icon className={cn('size-6', accent.icon)} />
        </div>
        {feature.badge === 'beta' && (
          <Badge
            variant="outline"
            className="border-warning/40 bg-warning/15 text-warning-muted-foreground shrink-0 text-[10px] uppercase tracking-wide"
          >
            Beta
          </Badge>
        )}
      </div>
      <h3 className="text-text-primary mb-2 text-xl">{feature.title}</h3>
      <p className="text-text-muted">{feature.description}</p>
    </SpotlightCard>
  )
}

export function FeaturesSection() {
  return (
    <section id="features" className="container mx-auto px-4 py-20">
      <div className="mx-auto max-w-6xl">
        <h2 className="text-text-primary mb-16 text-center text-4xl md:text-5xl">
          Everything You Need to Play
        </h2>
        <div className="grid gap-8 md:grid-cols-3">
          {FEATURES.map((feature) => (
            <FeatureCard key={feature.id} feature={feature} />
          ))}
        </div>
      </div>
    </section>
  )
}

/**
 * Deliberately not a SpotlightCard: no glow, no accent hue, one surface level
 * dimmer and a step down in type. The roadmap should read as subordinate to
 * the features grid it sits under.
 */
function ComingSoonCard({ item }: { item: ComingSoonItem }) {
  const Icon = item.icon

  return (
    <article className="border-border-default/60 bg-surface-1/30 hover:border-border-strong/60 rounded-2xl border border-dashed p-5 backdrop-blur-sm transition-colors">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="bg-surface-2/60 flex size-10 shrink-0 items-center justify-center rounded-lg">
          <Icon className="text-text-muted size-5" />
        </div>
        <Badge
          variant="outline"
          className="border-border-default/60 bg-surface-2/60 text-text-muted shrink-0 text-[10px] uppercase tracking-wide"
        >
          {COMING_SOON_STATUS_LABELS[item.status]}
        </Badge>
      </div>
      <h3 className="text-text-secondary mb-1 text-base font-semibold">
        {item.title}
      </h3>
      <p className="text-text-muted text-sm">{item.description}</p>
    </article>
  )
}

export function ComingSoonSection() {
  return (
    <section id="coming-soon" className="container mx-auto px-4 py-16">
      <div className="mx-auto max-w-6xl">
        <h2 className="text-text-secondary mb-4 text-center text-3xl md:text-4xl">
          Coming Soon
        </h2>
        <p className="text-text-muted mx-auto mb-12 max-w-2xl text-center">
          What we&apos;re building next. Plans and timing may change.
        </p>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {COMING_SOON.map((item) => (
            <ComingSoonCard key={item.id} item={item} />
          ))}
        </div>
      </div>
    </section>
  )
}
